/* eslint-disable react/forbid-elements -- TODO: extract map page inline HTML to feature components */
/* eslint-disable complexity -- TODO: extract map page logic into smaller hooks */
/* eslint-disable max-lines -- TODO: extract map page logic into smaller hooks */
/* eslint-disable max-lines-per-function -- TODO: extract bulk-download wiring into a dedicated hook */
import * as React from 'react';
import { z } from 'zod';
import { createFileRoute, useNavigate, useRouteContext, useSearch } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import type { DrawingTool } from '@ooh/ui/components/map-view';
import { useHasPermission } from '../../hooks/use-has-permission';
import { useHasNarrowedVisibility } from '../../hooks/use-has-narrowed-visibility';
import { resolveEmptyMessage } from '../../features/media-units/lib/resolve-empty-message';
import { useOfferContext } from '../../features/offers/add-via-catalog/hooks/use-offer-context';
import { useOfferContextNavigation } from '../../features/offers/add-via-catalog/hooks/use-offer-context-navigation';
import { OfferContextBar } from '../../features/offers/add-via-catalog/components/offer-context-bar';
import { MapPinPopupCardContainer } from '../../features/media-units/components/map-pin-popup-card-container';
import {
  QuickAddOfferPicker,
  buildQuickAddPickerLabels,
  getInitialSelectedDraftId,
  MAP_QUICK_ADD_PICKER_BASE_KEY,
  TransportLineInfoPopup,
  normalizeClientList,
  useQuickAddPickerData,
} from '@ooh/screens';
import { useQuickAddPipeline } from '../../features/media-units/components/use-quick-add-pipeline';
import { Button } from '@ooh/ui/components/button';
import { PageTitleContext } from '../__root';
import { mediaTypeColors } from '../../lib/map-config';
import { SHARED_FILTER_FIELD_IDS } from '../../lib/filter-config';
import type { SharedFilterFieldId } from '../../lib/filter-config';
import { MapLegend } from '../../components/map-legend';
import { MapFilterToolbar } from '../../components/map-filter-toolbar';
import { addToOfferIdSearchField } from '../../lib/offer-context-search-schema';
import { PoiLayerOverlay } from '../../features/pois/components/poi-layer-overlay';
import { useMapPoiState } from '../../features/pois/hooks/use-map-poi-state';
import { usePoiPopupRenderer } from '../../features/pois/hooks/use-poi-popup-renderer';
import { useTransportLayerState } from '../../features/transport-layers/hooks/use-transport-layer-state';
import { TransportLayerOverlay } from '../../features/transport-layers/components/transport-layer-overlay';
import { useAdministrativeAreaLayerState } from '../../features/administrative-areas/hooks/use-administrative-area-layer-state';
import { AdministrativeAreaLayerOverlay } from '../../features/administrative-areas/components/administrative-area-layer-overlay';
import {
  CITY_ZOOM_THRESHOLD,
  ROAD_ZOOM_THRESHOLD,
} from '../../features/transport-layers/constants';
import { useAreaSelection } from '../../features/map-area-selection';
import {
  DrawingToolPalette,
  DrawProgressBadge,
  SelectionSummaryOverlay,
} from '../../features/map-area-selection/components';
import { MapViewWithDrawing } from '../../features/map-area-selection/lib';
import {
  useMapUnifiedSearch,
  useFitBoundsToPins,
  useMapCoordinateSearchEnabled,
  MapSearchHeader,
} from '../../features/map-search';
import { urlLatLngToMarker } from '../../features/map-search/lib/url-lat-lng-marker';
import { RANGE_FIELD_DEFS, type RangeFieldDef } from '../../lib/range-field-defs';
import {
  CATALOG_ONLY_REAL_ARRAY_FIELD_IDS,
  CATALOG_ONLY_SCALAR_TEXT_FIELD_IDS,
} from '../../features/media-units/lib/filter-fields-catalog-only';
import { useMapPageState } from './use-map-page-state';
import { resolveInitialBounds } from './resolve-initial-bounds';
import { useTransportGeoJson } from './use-transport-geojson';
import { useTransportLegendEntries } from './use-transport-legend-entries';
import { useMapPinsData } from './use-map-pins-data';
import { useAreaSelectionHandlers } from './use-area-selection-handlers';
import { useClientsList } from '../../features/clients/api';
import { useCampaignNumberPreview, useOfferSalesUsers } from '../../features/offers/api';
import { BULK_DOWNLOAD_MAX_UNITS } from '@ooh/contracts';
import type { CampaignNumberPreviewQuery } from '@ooh/contracts';
import { useBulkDownloadSpecs } from '../../features/media-units/hooks/use-bulk-download-specs';
import { BulkDownloadConfirmDialog } from '../../features/media-units/components/bulk-download-confirm-dialog';

// Catalog-only array/scalar-text/range filter fields (material, address, area_min,
// catalogPrice_max, etc. — map-catalog-filter-parity) generated the SAME way the base
// shape above is generated, so a future addition to CATALOG_ONLY_REAL_ARRAY_FIELD_IDS,
// CATALOG_ONLY_SCALAR_TEXT_FIELD_IDS, or RANGE_FIELD_DEFS can never drift out of sync
// with this schema. Without this, Zod strips every one of these keys from the URL on
// every route parse (unknown keys are dropped by default), silently breaking
// persistence even when the rest of the filter wiring is correct.
const catalogOnlyArrayFieldsShape = Object.fromEntries(
  CATALOG_ONLY_REAL_ARRAY_FIELD_IDS.map((id) => [id, z.array(z.string()).optional()]),
) as Record<
  (typeof CATALOG_ONLY_REAL_ARRAY_FIELD_IDS)[number],
  z.ZodOptional<z.ZodArray<z.ZodString>>
>;
const catalogOnlyScalarTextFieldsShape = Object.fromEntries(
  CATALOG_ONLY_SCALAR_TEXT_FIELD_IDS.map((id) => [id, z.string().optional()]),
) as Record<(typeof CATALOG_ONLY_SCALAR_TEXT_FIELD_IDS)[number], z.ZodOptional<z.ZodString>>;
// `RangeFieldDef.minKey`/`maxKey` are deliberately widened to `keyof MediaUnitFilters`
// (range-field-defs.ts, generated from COST_RANGE_DIMENSIONS) — casting against that
// broad type here would make TS believe every unrelated MediaUnitFilters key (e.g.
// `region`, `exclude`) is also present with a numeric type, silently overriding their
// real declared types via `.extend()`. `def.id` stays a genuine finite literal union
// (`RangeFieldDef['id']`), and every `minKey`/`maxKey` value is exactly
// `${id}_min`/`${id}_max`, so the template-literal union below is both precise and safe.
type RangeFieldId = RangeFieldDef['id'];
const rangeFieldsShape = Object.fromEntries(
  RANGE_FIELD_DEFS.flatMap((def) => [
    [def.minKey, z.coerce.number().optional()],
    [def.maxKey, z.coerce.number().optional()],
  ]),
) as Record<`${RangeFieldId}_min` | `${RangeFieldId}_max`, z.ZodOptional<z.ZodNumber>>;

export const mapSearchSchema = z
  .object(
    Object.fromEntries(
      SHARED_FILTER_FIELD_IDS.map((id) => [id, z.array(z.string()).optional()]),
    ) as { [K in SharedFilterFieldId]: z.ZodOptional<z.ZodArray<z.ZodString>> },
  )
  .extend({
    ...catalogOnlyArrayFieldsShape,
    ...catalogOnlyScalarTextFieldsShape,
    ...rangeFieldsShape,
    // "bez zdjęcia" boolean sidecar — coerceNoPhoto (sidecar-coercion.ts) accepts a real
    // boolean or the strings "true"/"1"; the schema only needs to pass either shape through.
    noPhoto: z.union([z.boolean(), z.string()]).optional(),
    availabilityFrom: z.string().optional(),
    availabilityTo: z.string().optional(),
    nearKey: z.string().optional(),
    nearValue: z.string().optional(),
    nearRadius: z.coerce.number().int().positive().optional(),
    nearPoiId: z.string().uuid().optional(),
    lat: z.coerce.number().optional(),
    lng: z.coerce.number().optional(),
    zoom: z.coerce.number().int().positive().optional(),
    // Last-persisted map viewport bbox ("west,south,east,north"), restored on
    // arrival so panning/zooming survives navigating away and back (CU-86cb1nq31).
    // Distinct from lat/lng/zoom above, which are the coordinate-search deep-link
    // params (catalog "open on map" / POI "show on map") — see use-map-page-state.ts.
    viewBbox: z.string().optional(),
    // Unified text/coordinate search term, shared with the catalog `search` param.
    search: z.string().optional(),
    // Offer-context param shared with the catalog route (`validateMediaUnitsSearch`)
    // and `/media-units/add`; resolved to an editable DRAFT offer summary by
    // `useOfferContext`, or ignored. See offer-context-search-schema.ts for the
    // full leniency rationale.
    addToOfferId: addToOfferIdSearchField,
  });

export const Route = createFileRoute('/map/')({
  validateSearch: mapSearchSchema,
  component: MapPage,
});

export function MapPage() {
  const search = useSearch({ strict: false }) as {
    lat?: number;
    lng?: number;
    zoom?: number;
    addToOfferId?: string;
  };
  const { lat, lng, zoom, addToOfferId } = search;
  const { auth } = useRouteContext({ from: '__root__' });
  const { setPageTitle } = React.useContext(PageTitleContext);
  const { t, i18n } = useTranslation('map');
  const { t: tInventory } = useTranslation('inventory');
  const navigate = useNavigate({ from: Route.fullPath });
  const { offerContext } = useOfferContext(addToOfferId);
  const { handleReturnToOffer, handleExitOfferContext } = useOfferContextNavigation(
    offerContext,
    navigate,
  );
  const canQuickAddToOffer = useHasPermission('offer.prepare', { loading: 'closed' });
  const canDownloadSpecs = useHasPermission('media_unit.view');
  const canAssignOtherSalesperson = useHasPermission('offer.assign_other_salesperson');
  const canCreateClient = useHasPermission('client.create');
  // Whether the caller's own permission scope genuinely narrows what they can
  // see — feeds the zero-results empty state's permission-possible-cause
  // caveat, shared with Katalog nośników via resolveEmptyMessage (CU-86cbenzvw).
  const hasNarrowedVisibility = useHasNarrowedVisibility();
  // FAIL-CLOSED gate: the flaky coordinate search renders only when the
  // map-coordinate-search feature flag is explicitly enabled (hidden in prod).
  const isMapCoordinateSearchEnabled = useMapCoordinateSearchEnabled();
  const {
    start: startBulkDownload,
    pendingManifest: bulkDownloadPendingManifest,
    confirmDownload,
    cancelDownload,
  } = useBulkDownloadSpecs();
  const clientsQuery = useClientsList();
  const salesUsersQuery = useOfferSalesUsers();
  const availableClients = React.useMemo(
    () => normalizeClientList(clientsQuery.data),
    [clientsQuery.data],
  );
  const clientOptions = React.useMemo(
    () =>
      availableClients.map((client) => ({
        label: client.name,
        value: String(client.id),
        brands: client.brands,
        acronym: client.acronym,
      })),
    [availableClients],
  );
  // See media-units/index.tsx's identical comment: QuickAddOfferPicker
  // reports its "creating" mode selection here rather than calling
  // useCampaignNumberPreview itself (offer-draft-numbering / "Campaign-number
  // preview on offer creation forms"). Shared by both the map-pin popup
  // picker (via MapPinPopupCardContainer) and the direct selection picker
  // below — only one is ever open at a time.
  const [campaignNumberPreviewInput, setCampaignNumberPreviewInput] =
    React.useState<CampaignNumberPreviewQuery | null>(null);
  const campaignNumberPreview = useCampaignNumberPreview(campaignNumberPreviewInput);
  const salesUsers = React.useMemo(() => salesUsersQuery.data ?? [], [salesUsersQuery.data]);

  const [activeTool, setActiveTool] = React.useState<DrawingTool>('pan');
  const [hasShapes, setHasShapes] = React.useState(false);
  const { selectedIds, count, isEmpty: isSelectionEmpty, actions } = useAreaSelection();
  const handleShapeDrawn = React.useCallback(() => setHasShapes(true), []);
  const handleShapesCleared = React.useCallback(() => setHasShapes(false), []);

  const {
    showFilters,
    setShowFilters,
    filterValue,
    setFilterValue,
    poiFilterParams,
    setPoiFilterParams,
    noPhoto,
    setNoPhoto,
    availabilityRange,
    setAvailabilityRange,
    mapBbox,
    setMapBbox,
    initialViewBbox,
    visiblePoiCategories,
    setVisiblePoiCategories,
    mapInstance,
    setMapInstance,
    currentZoom,
    setCurrentZoom,
    linePopup,
    setLinePopup,
  } = useMapPageState();

  // Unified search: same URL `search` param + behavior as the catalog. A
  // coordinate term or geocoded POI drops a marker + eases the camera; a text
  // term with map-text-search ON is forwarded to the pins query (legacy path);
  // a text term with map-text-search OFF is sent to the geocoder (default path).
  const {
    searchValue,
    setSearchValue,
    searchFilter,
    markerPosition: searchMarkerPosition,
    // isGeocodingLoading and isGeocodingError are available for future UX use
    // (e.g. loading spinner in the search bar, "lokalizacja nie znaleziona" toast).
  } = useMapUnifiedSearch({ mapInstance });

  // Seed an initial marker from valid lat/lng URL params so opening the map by
  // coordinates (catalog "open on map" + POI "show on map") drops a marker, not
  // just centers the camera. The in-map search marker takes precedence so the
  // coordinate search box still works after arrival.
  //
  // Captured once via lazy useState (not a live useMemo on lat/lng): lat/lng
  // get cleared from the URL lazily, atomically with the first real
  // onBoundsChange event (see setMapBbox in use-map-page-state.ts), and
  // recomputing this from the now-cleared live values would make the marker
  // disappear as soon as that happens, defeating the very affordance this
  // marker exists for (CU-86cb1nq31).
  const [urlMarkerPosition] = React.useState(() => urlLatLngToMarker(lat, lng));
  const markerPosition = searchMarkerPosition ?? urlMarkerPosition;

  // Restore the last-persisted viewport on arrival — but only when the user
  // did NOT just click a coordinate-search deep link (lat/lng present), which
  // always represents where the user explicitly wants to look and must win
  // over a stale remembered bbox from a previous session (CU-86cb1nq31).
  //
  // lat/lng are intentionally NOT stripped from the URL here on mount (that
  // was tried and reverted — see use-map-page-state.ts's setMapBbox for why
  // it raced the map's own initial-camera setup under React StrictMode and
  // broke coordinate-deep-link navigation entirely). They get stripped
  // lazily, atomically with the first real onBoundsChange event instead,
  // which only ever fires once the map has genuinely rendered with them.
  const initialBounds = React.useMemo(
    () => resolveInitialBounds(lat, lng, initialViewBbox),
    [lat, lng, initialViewBbox],
  );

  const { poiFeatures, setSelectedPoiId, selectedPoi, selectedPoiCategory } = useMapPoiState({
    auth,
    bbox: mapBbox,
    visiblePoiCategories,
  });
  const {
    isPublicEnabled,
    isRoadsEnabled,
    togglePublic,
    toggleRoads,
    transportLines,
    isTransportLinesLoading,
  } = useTransportLayerState({ auth, bbox: mapBbox });
  const transportGeoJson = useTransportGeoJson(transportLines);
  const transportLegendEntries = useTransportLegendEntries(isPublicEnabled, isRoadsEnabled);
  const {
    isEnabled: isAdminAreaEnabled,
    toggle: toggleAdminArea,
    areas: adminAreas,
    isLoading: isAdminAreaLoading,
    isError: isAdminAreaError,
  } = useAdministrativeAreaLayerState({ auth, bbox: mapBbox });
  // Selected administrative-area IDs sourced from the shared URL filter — the overlay
  // uses this ONLY for feature-state highlighting; it never reads/writes this filter.
  const selectedAdminAreaIds = filterValue.administrativeAreaIds ?? [];
  const {
    mapPins,
    filterFields,
    filterPanelLabels,
    rangeLabels,
    panelFilterValue,
    handlePanelFiltersChange,
    isLoading,
    isError,
    refetch,
    data,
  } = useMapPinsData(
    auth,
    filterValue,
    poiFilterParams,
    searchFilter,
    setFilterValue,
    noPhoto,
    availabilityRange,
  );
  // Fit the camera to the matching pins once a text search resolves.
  useFitBoundsToPins(mapInstance, searchFilter, mapPins);
  const {
    mapViewRef,
    drawProgressCount,
    handleDrawComplete,
    handleDrawProgress,
    handleClearShapes,
    handleAddToDraft,
    isPickerOpen,
    pickerMediaUnitIds,
    closePicker,
    handleAddToDraftSuccess,
  } = useAreaSelectionHandlers(mapPins, actions, {
    onShapeDrawn: handleShapeDrawn,
    onShapesCleared: handleShapesCleared,
  });

  // Shared quick-add-to-offer flow (CU-86c8zc5uw), reused for area selection.
  const translatePickerLabel = React.useCallback((key: string) => t(key as never), [t]);
  // Locked-target mode (offerContext present) pins the picker to that offer and
  // synthesizes its own single-entry draft, so the drafts list goes unused.
  const isOfferContextLocked = offerContext != null;
  const { draftsData, lastUsedData } = useQuickAddPickerData({ enabled: !isOfferContextLocked });
  const {
    confirm: confirmQuickAdd,
    openFullForm: openQuickAddFullForm,
    navigateToNewOffer: navigateToNewQuickAddOffer,
  } = useQuickAddPipeline();
  const drafts = draftsData?.drafts ?? [];
  const initialSelectedDraftId = getInitialSelectedDraftId(draftsData, lastUsedData);
  const [selectedDraftId, setSelectedDraftId] = React.useState<string | null>(
    initialSelectedDraftId,
  );
  React.useEffect(() => {
    setSelectedDraftId(initialSelectedDraftId);
  }, [initialSelectedDraftId]);

  React.useEffect(() => {
    if (linePopup !== null && !isPublicEnabled && !isRoadsEnabled) setLinePopup(null);
  }, [isPublicEnabled, isRoadsEnabled, linePopup, setLinePopup]);
  React.useEffect(() => {
    setPageTitle(t('pageTitle'));
  }, [setPageTitle, t]);

  const activeFilterCount = Object.values(filterValue).filter((v) => v.length > 0).length;
  const showEmptyState = !isLoading && !isError && mapPins.length === 0;
  const nearPoiName = React.useMemo(
    () => poiFeatures.find((p) => p.id === poiFilterParams.nearPoiId)?.name,
    [poiFeatures, poiFilterParams.nearPoiId],
  );
  const renderPoiPopup = usePoiPopupRenderer({
    poiFeatures,
    selectedPoiCategory,
    setPoiFilterParams,
    setSelectedPoiId,
  });
  const isEmpty = !isTransportLinesLoading && (transportLines?.data.length ?? 0) === 0;
  const visibleSelectedCount = React.useMemo(() => {
    if (isSelectionEmpty) return 0;
    const s = new Set(mapPins.map((p) => String(p.id)));
    let c = 0;
    for (const id of selectedIds) {
      if (s.has(String(id))) c++;
    }
    return c;
  }, [mapPins, selectedIds, isSelectionEmpty]);

  if (isLoading && !data)
    return (
      <div className="flex items-center justify-center h-full" data-testid="map-loading">
        <div className="h-6 w-6 rounded-full border-2 border-muted-foreground border-t-transparent animate-spin" />
      </div>
    );
  if (isError)
    return (
      <div
        className="flex flex-col items-center justify-center h-full gap-4"
        data-testid="map-error"
      >
        <p className="text-destructive text-[14px]">{t('errorState')}</p>
        <Button
          type="button"
          size="xs"
          onClick={() => void refetch()}
          data-testid="map-retry-button"
        >
          {t('retryButton')}
        </Button>
      </div>
    );

  return (
    <div className="flex flex-col h-full" style={{ height: 'calc(100vh - 3.5rem)' }}>
      {offerContext !== null ? (
        <OfferContextBar
          message={t('offerContext.bar.message', { offerName: offerContext.offerName })}
          returnLabel={t('offerContext.bar.returnLabel')}
          exitAriaLabel={t('offerContext.bar.exitAriaLabel')}
          onReturnToOffer={handleReturnToOffer}
          onExit={handleExitOfferContext}
        />
      ) : null}
      <MapFilterToolbar
        showFilters={showFilters}
        setShowFilters={setShowFilters}
        filterFields={filterFields}
        filterValue={panelFilterValue}
        setFilterValue={handlePanelFiltersChange}
        filterPanelLabels={filterPanelLabels}
        rangeLabels={rangeLabels}
        activeFilterCount={activeFilterCount}
        pinCount={mapPins.length}
        poiFilterParams={poiFilterParams}
        setPoiFilterParams={setPoiFilterParams}
        nearPoiFilterLabel={t('pois.filterSection.label')}
        nearPoiName={nearPoiName}
        noPhoto={noPhoto}
        setNoPhoto={setNoPhoto}
        availabilityRange={availabilityRange}
        setAvailabilityRange={setAvailabilityRange}
        noPhotoFilterLabel={t('filters.noPhoto')}
        availabilityFilterLabel={t('filters.availability')}
        onClearSearch={() => setSearchValue('')}
        t={(key, options) => t(key as never, options as never) as unknown as string}
        i18n={i18n}
      />
      {isMapCoordinateSearchEnabled ? (
        <MapSearchHeader
          value={searchValue}
          onChange={setSearchValue}
          placeholder={tInventory('filters.searchBox.placeholder')}
          ariaLabel={tInventory('filters.searchBox.ariaLabel')}
        />
      ) : null}
      <div className="relative flex-1 min-h-0">
        <MapViewWithDrawing
          mapViewRef={mapViewRef}
          activeTool={activeTool}
          actions={actions}
          selectedIds={selectedIds}
          handleDrawComplete={handleDrawComplete}
          handleDrawProgress={handleDrawProgress}
          mapPins={mapPins}
          poiFeatures={poiFeatures}
          visiblePoiCategories={visiblePoiCategories}
          categoryColors={mediaTypeColors}
          lat={lat}
          lng={lng}
          zoom={zoom}
          initialBounds={initialBounds ?? undefined}
          // Raise the clustering zoom threshold and shrink the radius so nearby /
          // two-sided units (near-identical coordinates) separate into distinct
          // pins well before MapLibre's practical max zoom (~22), instead of
          // staying merged under MapView's shared default (clusterMaxZoom=14,
          // clusterRadius=60) — see CU-86cb1nq3g.
          //
          // MUST stay strictly below 18: MapLibre's GeoJSONSource defaults its
          // own tile-loading `maxzoom` to 18 (independent of `clusterMaxZoom`,
          // see maplibre-gl's source.ts `this.maxzoom = 18`) and never requests
          // tiles beyond it — camera zoom past 18 just overzooms the last z18
          // tile. Setting clusterMaxZoom===18 (or higher) means that z18 tile
          // is itself still fully clustered, so pins never separate at ANY
          // camera zoom. clusterMaxZoom must resolve within an already-loaded
          // tile (<=17) for unclustered points to actually render once zoomed
          // in further.
          clusterMaxZoom={16}
          clusterRadius={30}
          markerPosition={markerPosition}
          renderPopup={(pin, onClose) => (
            <MapPinPopupCardContainer
              pinId={pin.id}
              onClose={onClose}
              canQuickAdd={canQuickAddToOffer}
              clientOptions={clientOptions}
              salesUsers={salesUsers}
              authUserId={auth.user?.id}
              canAssignOtherSalesperson={canAssignOtherSalesperson}
              canCreateClient={canCreateClient}
              campaignNumberPreview={campaignNumberPreview}
              onCampaignNumberPreviewInputChange={setCampaignNumberPreviewInput}
              isPublicEnabled={isPublicEnabled}
              isRoadsEnabled={isRoadsEnabled}
              nearbyTitle={t('transportLayers.nearbyTitle')}
              lockedTarget={offerContext}
            />
          )}
          onPoiClick={setSelectedPoiId}
          selectedPoiId={selectedPoi?.id ?? null}
          renderPoiPopup={renderPoiPopup}
          onBoundsChange={setMapBbox}
          onMapReady={(map) => {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-member-access
            (window as any).__mapInstance = map;
            setMapInstance(map);
            setCurrentZoom(map.getZoom());
            map.on('zoomend', () => {
              setCurrentZoom(map.getZoom());
            });
          }}
        />
        <DrawingToolPalette
          activeTool={activeTool}
          hasSelection={hasShapes || !isSelectionEmpty}
          onToolChange={setActiveTool}
          onClearShapes={handleClearShapes}
          t={(key) => t(key as never)}
        />
        {drawProgressCount !== null ? <DrawProgressBadge count={drawProgressCount} /> : null}
        {!isSelectionEmpty ? (
          <SelectionSummaryOverlay
            selectedCount={count}
            visibleSelectedCount={visibleSelectedCount}
            selectedIds={selectedIds}
            onAddToDraft={canQuickAddToOffer ? handleAddToDraft : undefined}
            onClearSelection={actions.clear}
            // Cast required: react-i18next narrows t() return to a key union; the component prop accepts plain string
            t={(key, options) => t(key as never, options as never) as unknown as string}
            onBulkDownloadSpecs={
              canDownloadSpecs ? (ids) => void startBulkDownload(ids) : undefined
            }
            bulkDownloadDisabled={count === 0 || count > BULK_DOWNLOAD_MAX_UNITS}
            bulkDownloadDisabledReason={
              count === 0
                ? (tInventory('bulkDownload.trigger.disabledEmpty') as string)
                : count > BULK_DOWNLOAD_MAX_UNITS
                  ? (tInventory('bulkDownload.trigger.disabledCap') as string)
                  : undefined
            }
          />
        ) : null}
        {bulkDownloadPendingManifest ? (
          <BulkDownloadConfirmDialog
            manifest={bulkDownloadPendingManifest}
            totalSelectedUnits={count}
            onConfirm={() => void confirmDownload()}
            onCancel={cancelDownload}
          />
        ) : null}
        {isPickerOpen && canQuickAddToOffer ? (
          <QuickAddOfferPicker
            presentation="modal"
            mediaUnitIds={pickerMediaUnitIds}
            drafts={drafts}
            selectedDraftId={selectedDraftId}
            onSelectDraft={setSelectedDraftId}
            clientOptions={clientOptions}
            salesUsers={salesUsers}
            authUserId={auth.user?.id}
            canAssignOtherSalesperson={canAssignOtherSalesperson}
            canCreateClient={canCreateClient}
            campaignNumberPreview={campaignNumberPreview}
            onCampaignNumberPreviewInputChange={setCampaignNumberPreviewInput}
            createDraftMode="inline"
            lockedTarget={offerContext}
            onConfirm={confirmQuickAdd}
            onNavigateToNewOffer={navigateToNewQuickAddOffer}
            onSuccess={handleAddToDraftSuccess}
            onCancel={closePicker}
            onOpenFullForm={(args) => {
              closePicker();
              openQuickAddFullForm(args);
            }}
            {...buildQuickAddPickerLabels(translatePickerLabel, MAP_QUICK_ADD_PICKER_BASE_KEY)}
          />
        ) : null}
        <TransportLayerOverlay
          mapInstance={mapInstance}
          geoJson={transportGeoJson}
          isPublicEnabled={isPublicEnabled}
          isRoadsEnabled={isRoadsEnabled}
          isLoading={isTransportLinesLoading}
          isEmpty={isEmpty}
          onLineClick={setLinePopup}
        />
        <AdministrativeAreaLayerOverlay
          mapInstance={mapInstance}
          geoJson={adminAreas}
          isEnabled={isAdminAreaEnabled}
          selectedIds={selectedAdminAreaIds}
        />
        {linePopup !== null ? (
          <TransportLineInfoPopup
            label={linePopup.label}
            typeLabel={t(linePopup.typeLabel as never)}
            x={linePopup.x}
            y={linePopup.y}
            onClose={() => setLinePopup(null)}
            closeAriaLabel={t('transportLayers.lineInfo.closeAriaLabel')}
          />
        ) : null}
        {showEmptyState ? (
          <div
            className="absolute inset-0 flex items-center justify-center bg-background/60 backdrop-blur-sm z-10"
            data-testid="map-empty-state"
          >
            <p className="text-muted-foreground text-[14px]">
              {resolveEmptyMessage(undefined, hasNarrowedVisibility, tInventory)}
            </p>
          </div>
        ) : null}
        <PoiLayerOverlay
          visibleCategories={visiblePoiCategories}
          onVisibleCategoriesChange={setVisiblePoiCategories}
          transportPublicEnabled={isPublicEnabled}
          onTransportPublicToggle={togglePublic}
          roadsEnabled={isRoadsEnabled}
          onRoadsToggle={toggleRoads}
          showTransportZoomHint={
            isPublicEnabled && currentZoom !== null ? currentZoom < CITY_ZOOM_THRESHOLD : false
          }
          showRoadsZoomHint={
            isRoadsEnabled && currentZoom !== null ? currentZoom < ROAD_ZOOM_THRESHOLD : false
          }
          isTransportLoading={isPublicEnabled || isRoadsEnabled ? isTransportLinesLoading : false}
          isTransportEmpty={isEmpty}
          adminAreaEnabled={isAdminAreaEnabled}
          onAdminAreaToggle={toggleAdminArea}
          isAdminAreaLoading={isAdminAreaEnabled ? isAdminAreaLoading : false}
          isAdminAreaError={isAdminAreaEnabled ? isAdminAreaError : false}
        />
        <MapLegend transportLegendEntries={transportLegendEntries} />
      </div>
    </div>
  );
}
